import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CreditCard, Download, Check, AlertCircle, Clock, Loader2, Gift, Zap, Apple, RefreshCw } from 'lucide-react';
import { subscriptionAPI, getErrorMessage } from '../../lib/api';
import { toast } from '../../hooks/use-toast';

const Subscription = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionAccess, setSubscriptionAccess] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  // Fetch subscription data on mount
  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch both subscription access and history in parallel
      const [accessResponse, historyResponse] = await Promise.all([
        subscriptionAPI.getSubscriptionAccess(),
        subscriptionAPI.getSubscriptionHistory(),
      ]);

      setSubscriptionAccess(accessResponse.data.data);
      setSubscriptionHistory(historyResponse.data.data?.history || []);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (subscriptionId, invoiceNumber) => {
    setDownloadingInvoice(subscriptionId);
    try {
      const response = await subscriptionAPI.downloadInvoice(subscriptionId);
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoiceNumber || subscriptionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success({
        title: 'Invoice Downloaded',
        description: `Invoice ${invoiceNumber || subscriptionId} has been downloaded successfully.`,
      });
    } catch (err) {
      console.error('Error downloading invoice:', err);
      toast.error({
        title: 'Download Failed',
        description: getErrorMessage(err) || 'Failed to download invoice. Please try again.',
      });
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleNavigateToPricing = () => {
    toast.info({
      title: 'Redirecting to Pricing',
      description: 'Taking you to our pricing page to explore subscription plans.',
    });
    navigate('/pricing');
  };

  // Display date as-is from backend (backend sends DD-MM-YYYY format)
  // If date is in ISO format (YYYY-MM-DD), convert it to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // If already in DD-MM-YYYY format, return as-is
    if (/^\d{2}-\d{2}-\d{4}/.test(dateString)) {
      return dateString.split(' ')[0]; // Remove time if present
    }
    
    // If in ISO format (YYYY-MM-DD), convert to DD-MM-YYYY
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const [year, month, day] = dateString.split('T')[0].split('-');
      return `${day}-${month}-${year}`;
    }
    
    // Fallback: try to parse and format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Parse DD-MM-YYYY date string for calculations
  const parseDate = (dateString) => {
    if (!dateString) return null;
    
    // If in DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}/.test(dateString)) {
      const [day, month, year] = dateString.split(' ')[0].split('-');
      return new Date(year, month - 1, day);
    }
    
    // If in ISO format
    return new Date(dateString);
  };

  // Get current subscription from access data
  const currentSubscription = subscriptionAccess?.subscription;
  const hasActiveSubscription = currentSubscription?.is_active;
  
  // Get free tier access
  const freeTier = subscriptionAccess?.free_tier;
  const hasActiveFreeTier = freeTier?.is_active;
  
  // Get live ICA access from services
  const liveIca = subscriptionAccess?.services?.live_ica;
  
  // Calculate days remaining for free tier
  const getFreeTierDaysRemaining = () => {
    if (!freeTier?.expiry_date) return 0;
    const expiryDate = parseDate(freeTier.expiry_date);
    if (!expiryDate) return 0;
    const today = new Date();
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Get plan features based on plan category
  const getPlanFeatures = (planCategory) => {
    const features = [];
    
    if (planCategory === 'stock_recommendations') {
      features.push(
        'Research Based Stock Recommendations',
        'Entry & Exit Recommendations',
        'Regular Portfolio Reviews',
        'Email & WhatsApp Support'
      );
    } else if (planCategory === 'live_corporate_announcements') {
      features.push(
        'Real-time Corporate Announcements',
        'Custom Stock Alerts',
        'Index-based Filtering',
        'Priority Notifications'
      );
    } else {
      features.push('Access to Premium Features');
    }
    
    // Add Live Corporate Announcements Access if user has access
    if (liveIca?.has_access && planCategory !== 'live_corporate_announcements') {
      features.push('Live Corporate Announcements Access');
    }
    
    return features;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading subscription data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchSubscriptionData}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Free Tier Access Card - Show when user has active free tier */}
      {hasActiveFreeTier && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <Gift size={28} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-xl font-bold">Free Tier Access</h2>
                  <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full">
                    BONUS
                  </span>
                </div>
                <p className="text-emerald-100 text-sm mb-3">
                  Enjoy 2 months complimentary access to Live Corporate Announcements (Basic Plan)
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <div className="bg-white/10 rounded-lg px-4 py-2">
                    <p className="text-emerald-100 text-xs">Tier</p>
                    <p className="font-bold capitalize">{liveIca?.tier || 'Basic'}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg px-4 py-2">
                    <p className="text-emerald-100 text-xs">Expires On</p>
                    <p className="font-bold">{formatDate(freeTier?.expiry_date)}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg px-4 py-2">
                    <p className="text-emerald-100 text-xs">Days Remaining</p>
                    <p className="font-bold text-lg">{getFreeTierDaysRemaining()}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg px-4 py-2">
                    <p className="text-emerald-100 text-xs">Status</p>
                    <span className="inline-flex items-center gap-1 text-green-300 font-bold">
                      <Check size={14} />
                      Active
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-2 md:items-end">
              <button 
                onClick={handleNavigateToPricing}
                className="bg-white text-emerald-600 px-5 py-2 rounded-lg font-bold hover:bg-emerald-50 transition-colors shadow-md text-sm flex items-center gap-2"
              >
                <Zap size={16} />
                Upgrade to Premium
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live ICA Access Card - Show when user has live ICA access (from subscription or free tier) */}
      {liveIca?.has_access && (
        <div className="bg-white rounded-lg shadow-lg border border-purple-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="text-yellow-300" size={24} />
                <div>
                  <h3 className="text-lg font-bold text-white">Live Corporate Announcements Access</h3>
                  <p className="text-purple-200 text-sm">Real-time announcements from NSE & BSE</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                liveIca?.access_type === 'bonus_free_tier' 
                  ? 'bg-yellow-400 text-yellow-900' 
                  : 'bg-green-400 text-green-900'
              }`}>
                {liveIca?.access_type === 'bonus_free_tier' ? 'FREE TIER' : 'SUBSCRIBED'}
              </span>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-purple-600 text-xs font-semibold mb-1">Current Tier</p>
                <p className="text-xl font-bold text-purple-900 capitalize">{liveIca?.tier || '-'}</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-purple-600 text-xs font-semibold mb-1">Max Indexes</p>
                <p className="text-xl font-bold text-purple-900">{liveIca?.max_indexes ?? 'Unlimited'}</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-purple-600 text-xs font-semibold mb-1">Max Custom Stocks</p>
                <p className="text-xl font-bold text-purple-900">{liveIca?.max_custom_stocks ?? 'Unlimited'}</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-purple-600 text-xs font-semibold mb-1">Default Index</p>
                <p className="text-xl font-bold text-purple-900">{liveIca?.default_index || '-'}</p>
              </div>
            </div>
            
            {liveIca?.access_type === 'bonus_free_tier' && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} />
                  <div>
                    <p className="text-yellow-800 font-semibold text-sm">Free Tier Limitations</p>
                    <p className="text-yellow-700 text-sm mt-1">
                      Your free tier access has limited features. Upgrade to a premium plan for unlimited indexes, 
                      custom stock tracking, and advanced filtering options.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Current Subscription Card */}
      {hasActiveSubscription ? (
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow-lg p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <CreditCard size={32} />
                </div>
                <div>
                  <p className="text-blue-100 text-sm">Current Plan</p>
                  <h1 className="text-3xl font-bold">{currentSubscription.plan_name}</h1>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-blue-100 text-xs mb-1">Plan Category</p>
                  <p className="text-lg font-bold capitalize">
                    {currentSubscription.plan_category?.replace(/_/g, ' ') || '-'}
                  </p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-blue-100 text-xs mb-1">Expires On</p>
                  <p className="text-lg font-bold">{formatDate(currentSubscription.expiry_date)}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-blue-100 text-xs mb-1">Days Remaining</p>
                  <p className="text-2xl font-bold">{currentSubscription.days_remaining || 0}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="text-blue-100 text-xs mb-1">Status</p>
                  <span className="inline-flex items-center gap-1 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    <Check size={14} />
                    Active
                  </span>
                </div>
              </div>

              <div>
                <p className="text-blue-100 text-sm mb-3 font-semibold">Plan Features:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {getPlanFeatures(currentSubscription.plan_category).map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check size={16} className="text-green-300" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center gap-4">
              <button 
                onClick={handleNavigateToPricing}
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors shadow-lg flex items-center justify-center gap-2"
              >
                <CreditCard size={20} />
                Renew Subscription
              </button>
              <button 
                onClick={handleNavigateToPricing}
                className="border-2 border-white text-white px-6 py-3 rounded-lg font-bold hover:bg-white/10 transition-colors"
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>
      ) : (
        // No Active Subscription Card
        <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="p-4 bg-white/20 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <CreditCard size={40} />
            </div>
            <h2 className="text-2xl font-bold mb-2">No Active Subscription</h2>
            <p className="text-gray-300 mb-6">
              Subscribe to a plan to access premium features and services.
            </p>
            <button 
              onClick={handleNavigateToPricing}
              className="bg-yellow-400 text-gray-900 px-8 py-3 rounded-lg font-bold hover:bg-yellow-300 transition-colors shadow-lg"
            >
              View Plans & Subscribe
            </button>
          </div>
        </div>
      )}

      {/* Subscription History */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 border-b bg-gray-50">
          <h2 className="text-2xl font-bold text-gray-900">Subscription History</h2>
          <p className="text-gray-600 text-sm mt-1">View all your past and current subscriptions</p>
        </div>

        {subscriptionHistory.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Subscription History</h3>
            <p className="text-gray-600 mb-4">You haven't made any subscription purchases yet.</p>
            <button
              onClick={handleNavigateToPricing}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse Plans
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Payment Method</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Transaction ID</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Order ID</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Coupon</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Subscribed Date</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Expire Date</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Days Remaining</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Payment Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscriptionHistory.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold text-blue-600">{sub.plan_name}</span>
                        <div className="flex flex-wrap gap-1">
                          {sub.is_trial && (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                              Trial Plan
                            </span>
                          )}
                          {sub.is_ios_subscription && sub.is_trial_period && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                              Free Trial
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="font-semibold text-gray-900">₹{sub.amount_paid?.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                          sub.is_ios_subscription 
                            ? 'bg-gray-100 text-gray-800' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {sub.is_ios_subscription && <Apple size={12} />}
                          {sub.payment_method || 'Razorpay'}
                        </span>
                        {sub.is_ios_subscription && sub.auto_renew_enabled !== null && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                            sub.auto_renew_enabled 
                              ? 'bg-green-50 text-green-600' 
                              : 'bg-yellow-50 text-yellow-600'
                          }`}>
                            <RefreshCw size={10} />
                            {sub.auto_renew_enabled ? 'Auto-renew ON' : 'Auto-renew OFF'}
                          </span>
                        )}
                        {sub.is_ios_subscription && sub.apple_environment === 'sandbox' && (
                          <span className="inline-block px-2 py-0.5 rounded text-xs bg-orange-50 text-orange-600">
                            Sandbox
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-xs text-gray-600 font-mono">
                        {sub.is_ios_subscription && sub.apple_original_transaction_id 
                          ? sub.apple_original_transaction_id 
                          : (sub.transaction_id || '-')}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-xs text-gray-600 font-mono">{sub.order_id || '-'}</span>
                    </td>
                    <td className="px-4 py-4">
                      {sub.coupon_applied ? (
                        <span className="text-xs text-green-600 font-semibold">{sub.coupon_applied}</span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar size={12} />
                        {formatDate(sub.purchased_at)}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar size={12} />
                        {formatDate(sub.expiry_date)}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {sub.is_current && sub.days_remaining > 0 ? (
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                          <Clock size={12} />
                          {sub.days_remaining} days
                        </span>
                      ) : sub.is_expired ? (
                        <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold">
                          <AlertCircle size={12} />
                          Expired
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                        sub.payment_status === 'Successful' 
                          ? 'bg-green-100 text-green-700' 
                          : sub.payment_status === 'Pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {sub.payment_status === 'Successful' ? <Check size={12} /> : <AlertCircle size={12} />}
                        {sub.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {sub.is_current ? (
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                          <Check size={12} />
                          Current
                        </span>
                      ) : sub.is_expired ? (
                        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-semibold">
                          <AlertCircle size={12} />
                          Expired
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {sub.has_invoice && sub.payment_status === 'Successful' ? (
                        <button
                          onClick={() => handleDownloadInvoice(sub.id, sub.invoice_number)}
                          disabled={downloadingInvoice === sub.id}
                          className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900 transition-colors text-sm font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloadingInvoice === sub.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Download size={16} />
                          )}
                          {downloadingInvoice === sub.id ? 'Downloading...' : 'Download Invoice'}
                        </button>
                      ) : sub.payment_status === 'Successful' ? (
                        <span className="text-xs text-gray-400">Invoice generating...</span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Additional Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6">
          <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
            <AlertCircle size={20} />
            Subscription Benefits
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Get detailed research reports every month</li>
            <li>• Access to exclusive stock recommendations</li>
            <li>• Regular portfolio reviews and updates</li>
            <li>• Priority customer support</li>
          </ul>
        </div>

        <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-6">
          <h3 className="font-bold text-green-900 mb-2 flex items-center gap-2">
            <Check size={20} />
            Need Help?
          </h3>
          <p className="text-sm text-green-800 mb-2">
            For any subscription related queries, please contact our support team.
          </p>
          <a 
            href="mailto:support@climbcapital.in" 
            className="text-sm text-green-700 font-semibold hover:underline"
          >
            Contact Support →
          </a>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
